"use client";

import { useEffect, useRef, useState } from "react";

export default function MachineTab() {
  const [isHuman, setIsHuman] = useState(true);

  const dialogRef = useRef<HTMLDialogElement>(null);

  // A native modal dialog traps focus, closes on Escape and returns focus to the toggle.
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog || isHuman) return;

    dialog.showModal();
    document.body.style.overflow = "hidden";
    return () => {
      dialog.close();
      document.body.style.overflow = "";
    };
  }, [isHuman]);

  return (
    <>
      {/* Toggle Button */}
      <div className="fixed bottom-8 right-8 z-[60]">
        {/* Mobile Toggle */}
        <button
          type="button"
          aria-pressed={!isHuman}
          className="lg:hidden relative flex items-center px-4 py-1.5 bg-white/80 backdrop-blur-md rounded-full shadow-sm border border-gray-200"
          onClick={() => setIsHuman(false)}
        >
          <span className="relative z-10 text-xs font-medium text-gray-900">
            Machine
          </span>
        </button>

        {/* Desktop Toggle */}
        <div className="hidden lg:flex relative items-center p-0.5 bg-white/80 backdrop-blur-md rounded-full shadow-sm border border-gray-200">
          <div
            className="absolute top-0.5 bottom-0.5 rounded-full z-[2] transition-all duration-300 ease-in-out"
            style={{
              left: isHuman ? "2px" : "calc(50% + 1px)",
              width: "calc(50% - 3px)",
              background: "#B54921",
            }}
          />
          <button
            type="button"
            aria-pressed={isHuman}
            onClick={() => setIsHuman(true)}
            className={`relative z-10 px-4 py-1.5 text-xs font-medium rounded-full transition-colors duration-200 ${
              isHuman ? "text-white" : "text-gray-700"
            }`}
          >
            Human
          </button>
          <button
            type="button"
            aria-pressed={!isHuman}
            onClick={() => setIsHuman(false)}
            className={`relative z-10 px-4 py-1.5 text-xs font-medium rounded-full transition-colors duration-200 ${
              !isHuman ? "text-white" : "text-gray-700"
            }`}
          >
            Machine
          </button>
        </div>
      </div>

      {/* Machine View Modal */}
      <dialog
        ref={dialogRef}
        aria-labelledby="machine-view-title"
        onClose={() => setIsHuman(true)}
        className="m-auto max-h-[90vh] w-[calc(100%-2rem)] max-w-4xl flex-col overflow-hidden rounded-lg bg-white p-0 shadow-2xl backdrop:bg-black/50 backdrop:backdrop-blur-sm open:flex"
      >
        {!isHuman && (
          <>
              {/* Header */}
              <div
                className="flex-shrink-0 text-white px-6 py-4 flex items-center justify-between"
                style={{
                  background: "#B54921",
                }}
              >
                <div>
                  <h2
                    id="machine-view-title"
                    className="text-2xl font-bold text-white!"
                  >
                    Machine-Readable View
                  </h2>
                  <p className="text-sm text-white mt-1">
                    llms.txt - Information for AI agents
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setIsHuman(true)}
                  className="text-white hover:text-white/80 transition-colors"
                  aria-label="Close"
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-6">
                <pre className="bg-gray-50 p-6 rounded-lg border border-gray-200 text-sm font-mono text-gray-800 whitespace-pre-wrap break-words">
                  {`# Logical Health

> Logical Health is an AI-powered texting platform for healthcare organizations. It meets members in their everyday text messages — SMS, RCS, iMessage, WhatsApp — and turns one-way outreach into two-way conversations that identify and resolve the real barriers to care: benefits confusion, scheduling friction, transportation, and cost concerns. No app to download, no portal, no login.

## What Logical Health does

Healthcare organizations spend heavily on outreach members stopped seeing years ago: most emails go unopened, most calls go unanswered, and three in four members never open the plan app. Reminders tell members what to do but never find out why they didn't.

Logical Health analyzes claims and EMR signals to decide which members to reach and why, then sends branded, verified, two-way messages from the healthcare organization on whichever channel each member already uses. An AI agent holds a real conversation, diagnoses the barrier behind the member's reply, and resolves it in-thread — explaining the benefit, arranging the ride, calling the provider's office to book the appointment, or handing off to a human team when the situation calls for one. Every conversation ends in a recorded outcome, not an impression.

## How it works

1. **Verified sender.** Members receive messages from a branded, verified business profile, so they know who is contacting them.
2. **Conversational intake.** Instead of repeating a reminder, the AI asks the questions that surface the actual obstacle.
3. **Personalized action.** The member gets the information, options, and next steps needed to move forward without leaving the thread.
4. **Confirmed outcome.** Members book, confirm, reschedule, or complete the step by text, and the organization sees the resolution.

## Technology

- **Agentic AI.** Logical Health's agents do more than answer questions. They complete real-world tasks on the member's behalf, including calling provider offices to book appointments.
- **Proactive targeting.** Logical Health analyzes claims and EMR signals to determine which members to reach, when, and about what, so outreach is driven by evidence rather than by list blasts.
- **Explainable by design.** Built on Stanford research. Answers are instant, accurate, and explainable: a coverage answer traces back to the rule that produced it rather than to a black-box generation.
- **Auditability.** Every response is grounded in the organization's own rules and is fully traceable.
- **Multilingual.** Conversations happen in the member's preferred language, detected automatically.
- **Integration.** Connects to member data and works alongside the existing member-services stack. No rip-and-replace.
- **Human escalation.** Routes to the organization's team when a conversation needs a person.

## Use cases

- Preventive care — closing screening and wellness-visit gaps
- Gaps in care — finding and clearing the barrier behind each open gap
- Medication management — onboarding and adherence between visits
- Care navigation — guiding members to the right care at the right time
- Intake and logistics — from sign-up through first visit
- Renewals and enrollment — keeping members ahead of deadlines
- Wellness coaching — check-ins between visits
- Benefits activation — turning unused benefits into engagement

## Outcomes

- Improved HEDIS and Medicare Star performance
- Freed care-management capacity, with routine outreach handled automatically
- Reduced avoidable utilization through earlier steering to the right site of care
- Simpler benefits navigation and lower administrative contact volume
- Engagement with populations that do not respond to email, mail, or phone

Logical Health's [insurer case study](https://logical.health/blog/streamlining-call-center-operations) reports $16 million in savings from streamlining call center operations.

## Who it serves

Health plans (Medicare Advantage, Medicaid, ACA, commercial), third-party administrators, employers, and provider and care-management organizations.

## Compliance and security

HIPAA compliant. SOC 2 compliant. Encryption in transit and at rest, role-based least-privilege access, full audit logging, and a BAA signed on request. Texting is built to be compliant and still useful, rather than restricted until it stops working.

## Leadership

**Lauren Bilbo — Co-founder and CEO.** A software engineering manager at Google before founding Logical Health. She holds an MBA from the Stanford Graduate School of Business and an MSE in electrical engineering from the University of Michigan — a combination of engineering depth and operating experience at scale that is rare among healthcare AI founders.

**Baur Krykpayev — Co-founder and CTO.** A two-time founder who built AI GPU infrastructure at Meta and worked on core infrastructure at Google, with a background spanning AI, security, and large-scale systems. He studied at the University of Southern California.

## Company

Logical Health, Inc. is based in San Francisco, California. It is backed by a16z Speedrun and the family office of Chris Klomp, and its reasoning approach is built on Stanford research.

## Key pages

- [Home](https://logical.health): what Logical Health does, how it works, use cases, and impact
- [Book a demo](https://logical.health/#demo): schedule an intro call
- [Team](https://logical.health/team): the people behind Logical Health
- [Blog](https://logical.health/blog): writing on member engagement, AI, and healthcare operations
- [Trust center](https://logical.health/trust): security posture and compliance documentation
- [HIPAA compliance](https://logical.health/hipaa-compliance): healthcare security standards
- [Privacy policy](https://logical.health/legal#privacy-policy)
- [Terms of service](https://logical.health/legal#terms-of-service)
- [Accessibility statement](https://logical.health/accessibility): our accessibility target and how to report a barrier

## Contact

- Book a demo: https://logical.health/#demo
- **Email**: hello@logical.health
- **Phone**: +1 415-800-4642
- **LinkedIn**: https://linkedin.com/company/logical-health
- **Address**: Logical Health, Inc., 1509 Pershing Drive, Unit F, San Francisco, CA 94129, USA
- YouTube: https://www.youtube.com/@TheLogicalHealth
`}
                </pre>

                {/* Download Link */}
                <div className="mt-4 flex items-center justify-between bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <div className="flex items-center gap-3">
                    <svg
                      className="w-5 h-5 text-blue-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <p className="text-sm text-blue-800">
                      AI agents can access this information at{" "}
                      <code className="bg-blue-100 px-2 py-1 rounded text-xs">
                        /llms.txt
                      </code>
                    </p>
                  </div>
                  <a
                    href="/llms.txt"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm font-medium text-blue-600 hover:text-blue-700 flex items-center gap-1"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                      />
                    </svg>
                    View Raw
                  </a>
                </div>
              </div>
          </>
        )}
      </dialog>
    </>
  );
}
